import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useApiKeys';

const ALL_PERMISSIONS = [
  { value: 'events:read', label: 'Read Events' },
  { value: 'events:write', label: 'Write Events' },
  { value: 'guests:read', label: 'Read Guests' },
  { value: 'guests:write', label: 'Write Guests' },
  { value: 'rsvps:read', label: 'Read RSVPs' },
] as const;

export default function ApiKeysPage() {
  const { data: keys, isLoading, error } = useApiKeys();
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleTogglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  };

  const handleCreate = async () => {
    const result = await createMutation.mutateAsync({ name, permissions });
    setNewKey(result.plaintextKey);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setName('');
    setPermissions([]);
    setNewKey(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (newKey) {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
    }
  };

  const handleRevoke = async (keyId: string) => {
    await revokeMutation.mutateAsync(keyId);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load API keys. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <VpnKeyIcon color="primary" />
          <Typography variant="h5">API Keys</Typography>
        </Stack>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ minHeight: 44 }}
        >
          Create API Key
        </Button>
      </Stack>

      {keys && keys.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <VpnKeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No API Keys
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create an API key to integrate with the TimeToRSVP API.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {keys?.map((key) => (
          <Card key={key.id}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
              >
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {key.name}
                    </Typography>
                    <Chip
                      label={key.isActive ? 'Active' : 'Revoked'}
                      color={key.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {key.keyPrefix}...
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(key.createdAt).toLocaleDateString()}
                    </Typography>
                    {key.lastUsedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Last used: {new Date(key.lastUsedAt).toLocaleDateString()}
                      </Typography>
                    )}
                    {key.expiresAt && (
                      <Typography variant="caption" color="text.secondary">
                        Expires: {new Date(key.expiresAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {(key.permissions as string[]).map((perm) => (
                      <Chip key={perm} label={perm} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>

                {key.isActive && (
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={() => handleRevoke(key.id)}
                    disabled={revokeMutation.isPending}
                    sx={{ minHeight: 36, flexShrink: 0 }}
                  >
                    Revoke
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Create Key Dialog */}
      <Dialog open={dialogOpen} onClose={newKey ? undefined : handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{newKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
        <DialogContent>
          {newKey ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                Copy this API key now. You will not be able to see it again.
              </Alert>
              <TextField
                fullWidth
                value={newKey}
                slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: 14 } } }}
              />
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                sx={{ minHeight: 44 }}
              >
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Key Name"
                placeholder="e.g. Production API"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Permissions
                </Typography>
                <FormGroup>
                  {ALL_PERMISSIONS.map((perm) => (
                    <FormControlLabel
                      key={perm.value}
                      control={
                        <Checkbox
                          checked={permissions.includes(perm.value)}
                          onChange={() => handleTogglePermission(perm.value)}
                        />
                      }
                      label={perm.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {newKey ? (
            <Button onClick={handleCloseDialog}>Done</Button>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={!name || permissions.length === 0 || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
